import { useEffect, useRef, useState } from 'react';
import { Flex, Icon } from '@kognitos/lattice';

import { useAuth } from '@/shared/auth';
import { isE2ETestMode } from '@/shared/auth/utils/e2e';
import {
  frameCache,
  subscribeToScreencast,
} from '@/shared/components/ScreencastViewer/screencast-stream';

interface IBrowserSessionThumbnailProps {
  /** Screencast SSE URL, or null when it cannot be resolved. */
  screencastUrl: string | null;
  /**
   * Follow new frames. Only set while another player already holds the
   * stream, so the thumbnail shares that connection instead of opening one.
   */
  live: boolean;
}

/**
 * Small still of a browser session: the latest screencast frame, or a blank
 * browser glyph before the first one arrives.
 */
export function BrowserSessionThumbnail({
  screencastUrl,
  live,
}: IBrowserSessionThumbnailProps) {
  const { getAccessTokenSilently } = useAuth();
  const getAccessTokenSilentlyRef = useRef(getAccessTokenSilently);
  getAccessTokenSilentlyRef.current = getAccessTokenSilently;

  const [image, setImage] = useState<string | null>(() =>
    screencastUrl ? (frameCache.get(screencastUrl)?.image ?? null) : null,
  );

  // eslint-disable-next-line no-restricted-syntax -- reason: SSE subscription lifecycle
  useEffect(() => {
    setImage(
      screencastUrl ? (frameCache.get(screencastUrl)?.image ?? null) : null,
    );
    if (!screencastUrl || !live) return;
    const headers: Record<string, string> = isE2ETestMode()
      ? { 'X-E2E-Test-Mode': 'true' }
      : {};
    return subscribeToScreencast(
      screencastUrl,
      () => getAccessTokenSilentlyRef.current(),
      headers,
      { onFrame: frame => setImage(frame.image), onState: () => {} },
    );
  }, [screencastUrl, live]);

  return (
    <Flex
      align="center"
      justify="center"
      className="h-14 w-22 shrink-0 overflow-hidden rounded-lg border border-border bg-background"
      data-testid="browser-session-thumbnail"
    >
      {image ? (
        <img
          src={`data:image/jpeg;base64,${image}`}
          alt=""
          className="size-full object-cover object-top"
        />
      ) : (
        <Icon type="Globe" size="sm" className="text-muted-foreground" />
      )}
    </Flex>
  );
}
